import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import { twMerge } from 'tailwind-merge';

interface Props {
    onShowJoiningInfo: () => void;
    onShowJoinOnAnotherDevice: () => void;
    /**
     * Whether joining from here is on the table at all. Someone without the
     * video capability still wants the dial-in details, so the joining info
     * stays; the other-device link would be pointing at a door they cannot use.
     */
    showJoinOnAnotherDevice: boolean;
    /** Greyed out when the viewer cannot join this meeting at all (VID-309). */
    joinOnAnotherDeviceDisabled?: boolean;
}

/**
 * The two ways into the meeting's details, sitting beside the join button in a
 * colleague's lobby (VID-429). A guest is offered the other-device link on its
 * own below the row instead: they have one thing to do here, and a row of three
 * choices would bury it.
 *
 * Both open modals rather than folding a panel into the page, so the lobby
 * keeps its shape whether or not anyone opens them.
 */
const MeetingLobbyJoiningInfoButtons = ({
    onShowJoiningInfo,
    onShowJoinOnAnotherDevice,
    showJoinOnAnotherDevice,
    joinOnAnotherDeviceDisabled,
}: Props) => {
    const { i18next } = useAppContext();

    return (
        <>
            <button
                type="button"
                className="flex h-10 items-center gap-3 rounded-xl border px-3 text-sm font-medium text-gray-800"
                onClick={onShowJoiningInfo}
            >
                {/* Outlined, as the meeting's own joining-info controls are
                    (ButtonOpenCloseJoinInfo, MenuItemShowJoinInfo) — the same
                    glyph before and after joining. */}
                <Icon iconKey="infoOutlinedIcon" className="size-6" />
                {i18next.t('videoconference.lobby.joining_info')}
            </button>

            {showJoinOnAnotherDevice && (
                <button
                    type="button"
                    className={twMerge(
                        'h-10 rounded-xl border px-3 text-sm font-medium text-gray-800',
                        joinOnAnotherDeviceDisabled &&
                            'cursor-not-allowed text-gray-500',
                    )}
                    onClick={onShowJoinOnAnotherDevice}
                    disabled={joinOnAnotherDeviceDisabled}
                >
                    {i18next.t('videoconference.lobby.join_other_device')}
                </button>
            )}
        </>
    );
};

export default MeetingLobbyJoiningInfoButtons;
